/**
 * MODULE: _shared/frontend-shell/src/features/crm/data/scheduleModalCatalog.ts
 * PURPOSE: Mock owners and related-entity rows for ScheduleModal typeahead (Prompt 3-B)
 * DEPENDENCIES: ../types/activity.types, ./mockCrmData
 * PUBLIC_API: MOCK_SCHEDULE_OWNERS, buildScheduleEntityCatalog, searchScheduleEntities
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 177/250
 * RECENT: 2026-05-08 [2.2.876] CRM persistence UX compliance: persisted CRM create, edit, task, activity, deal, account, related-entity, and pipeline run-history flows; stale demo copy removed — root **`CHANGELOG` [2.2.876]`**
 */

import type { Account } from '@shared/lib/frontend/api/accountApi';
import type { ManagedContact } from '@shared/lib/frontend/api/contactApi';
import type { Activity } from '../types/activity.types';
import type { Opportunity } from '../types/opportunity.types';
import type { Task } from '../types/task.types';
import { MOCK_CRM_ACTIVITIES, MOCK_CRM_OPPORTUNITIES, MOCK_CRM_TASKS } from './mockCrmData.dev';

export type ScheduleRelatedEntityType = NonNullable<Task['relatedEntityType']>;

export type ScheduleEntityOption = {
  id: string;
  type: ScheduleRelatedEntityType;
  name: string;
  /** Optional second line in related-entity picker (e.g. email or account name). */
  subtitle?: string;
};

/** Same reference across renders — use while CRM lists are still loading so hooks do not re-fire on `[]` identity churn. */
export const EMPTY_SCHEDULE_ENTITY_CATALOG: ScheduleEntityOption[] = [];

const EXTRA_LABELS: Record<string, string> = {
  'con-12': 'Contact — roles update',
  'acct-7': 'Blue Yonder Airlines',
  'terr-west': 'West',
  'terr-mw': 'Midwest',
  'terr-east': 'East',
};

export const MOCK_SCHEDULE_OWNERS: { id: string; name: string }[] = [
  { id: 'user-1', name: 'Morgan Lee' },
  { id: 'user-2', name: 'Alex Kim' },
  { id: 'user-3', name: 'Jordan Patel' },
  { id: 'user-4', name: 'Sam Rivera' },
];

function dedupeKey(e: ScheduleEntityOption): string {
  return `${e.type}:${e.id}`;
}

/** Builds a stable catalog from opportunities plus task or activity relations not already covered. */
export function buildScheduleEntityCatalog(): ScheduleEntityOption[] {
  const map = new Map<string, ScheduleEntityOption>();

  for (const opp of MOCK_CRM_OPPORTUNITIES) {
    map.set(dedupeKey({ id: opp.id, type: 'deal', name: opp.name }), {
      id: opp.id,
      type: 'deal',
      name: opp.name,
    });
    map.set(dedupeKey({ id: opp.accountId, type: 'account', name: opp.accountName }), {
      id: opp.accountId,
      type: 'account',
      name: opp.accountName,
    });
    if (opp.primaryContactId && opp.primaryContactName) {
      map.set(
        dedupeKey({
          id: opp.primaryContactId,
          type: 'contact',
          name: opp.primaryContactName,
        }),
        {
          id: opp.primaryContactId,
          type: 'contact',
          name: opp.primaryContactName,
        }
      );
    }
    if (opp.territoryId && opp.territoryName) {
      map.set(
        dedupeKey({ id: opp.territoryId, type: 'territory', name: opp.territoryName }),
        { id: opp.territoryId, type: 'territory', name: opp.territoryName }
      );
    }
  }

  const addFromRelation = (id?: string, type?: ScheduleRelatedEntityType): void => {
    if (!id || !type) return;
    const key = dedupeKey({ id, type, name: '' });
    if (map.has(key)) return;
    const name = EXTRA_LABELS[id] ?? `${type} ${id}`;
    map.set(key, { id, type, name });
  };

  for (const t of MOCK_CRM_TASKS) {
    addFromRelation(t.relatedEntityId, t.relatedEntityType);
  }
  for (const a of MOCK_CRM_ACTIVITIES) {
    addFromRelation(a.relatedEntityId, a.relatedEntityType);
  }

  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}

let cachedCatalog: ScheduleEntityOption[] | null = null;

function getCatalog(): ScheduleEntityOption[] {
  if (!cachedCatalog) cachedCatalog = buildScheduleEntityCatalog();
  return cachedCatalog;
}

function contactDisplayName(c: ManagedContact): string {
  const fullName = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
  return fullName || c.email?.trim() || 'Contact';
}

function putOption(map: Map<string, ScheduleEntityOption>, option: ScheduleEntityOption): void {
  if (!option.id || !option.name.trim()) return;
  map.set(dedupeKey(option), option);
}

/**
 * Builds related-entity choices from already-loaded CRM rows (no HTTP).
 * Pass **`includeFallback: true`** only for Storybook or tests that need **`mockCrmData`** seed rows.
 */
export function buildCrmScheduleEntityCatalog(args: {
  contacts?: ManagedContact[];
  opportunities?: Opportunity[];
  accounts?: Account[];
  includeFallback?: boolean;
}): ScheduleEntityOption[] {
  const map = new Map<string, ScheduleEntityOption>();

  for (const c of args.contacts ?? []) {
    putOption(map, { id: c.id, type: 'contact', name: contactDisplayName(c) });
  }
  for (const o of args.opportunities ?? []) {
    putOption(map, { id: o.id, type: 'deal', name: o.name });
    if (o.accountId && o.accountName) {
      putOption(map, { id: o.accountId, type: 'account', name: o.accountName });
    }
    if (o.primaryContactId && o.primaryContactName) {
      putOption(map, { id: o.primaryContactId, type: 'contact', name: o.primaryContactName });
    }
  }
  for (const a of args.accounts ?? []) {
    putOption(map, { id: a.id, type: 'account', name: a.name });
  }

  if (args.includeFallback === true) {
    for (const fallback of getCatalog()) putOption(map, fallback);
  }

  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Case-insensitive substring filter on **`name`**, **`subtitle`**, and **`id`**. Default **`catalog`** is empty (no implicit mock list). */
export function searchScheduleEntities(query: string, limit = 12, catalog: ScheduleEntityOption[] = []): ScheduleEntityOption[] {
  const q = query.trim().toLowerCase();
  const all = catalog;
  if (!q) return all.slice(0, limit);
  return all
    .filter((e) => {
      const sub = (e.subtitle ?? '').toLowerCase();
      return e.name.toLowerCase().includes(q) || e.id.toLowerCase().includes(q) || sub.includes(q);
    })
    .slice(0, limit);
}

export function findEntityByKey(
  type: ScheduleRelatedEntityType,
  id: string
): ScheduleEntityOption | undefined {
  return getCatalog().find((e) => e.type === type && e.id === id);
}

export function ownerById(ownerId: string): { id: string; name: string } | undefined {
  return MOCK_SCHEDULE_OWNERS.find((o) => o.id === ownerId);
}

/** True when prefill looks like an Activity row (dueAtISO or subject + activity kind). */
export function prefillLooksLikeActivity(p: Partial<Task & Activity> | undefined): boolean {
  if (!p || typeof p !== 'object') return false;
  const act = p as Partial<Activity>;
  if (typeof act.dueAtISO === 'string') return true;
  const t = (p as { type?: unknown }).type;
  if (t === 'call' || t === 'email' || t === 'meeting' || t === 'note') return true;
  return t === 'task' && typeof act.subject === 'string';
}
